import IcHome from '@/assets/icons/ic-home.svg';
import IcStore from '@/assets/icons/ic-my-store.svg';
import IcWallet from '@/assets/icons/ic-wallet.svg';
import IcLineChart from '@/assets/icons/ic-bar-01.svg';
import IcHeart from '@/assets/icons/ic-heart.svg';
import IcCommunity from '@/assets/icons/ic-community.svg';
import IcFunnel from '@/assets/icons/ic-funnels.svg';
import IcLink from '@/assets/icons/ic-link.svg';
import IcClock from '@/assets/icons/ic-clock.svg';
import IcSmile from '@/assets/icons/ic-face-smile.svg';
import IcMail from '@/assets/icons/ic-mail.svg';
import IcSend from '@/assets/icons/ic-send-02.svg';
import IcPerson from '@/assets/icons/ic-profile.svg';
import IcSettings from '@/assets/icons/ic-settings.svg';
import IcAskStanley from '@/assets/icons/ic-stanley-profile-inverted.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';
import { buildMenuGroup } from '../utils/buildMenuGroup';
import { MenuItemType } from '@/types';

export const SCOPES = {
    ADMIN_IMPERSONATION: 'admin:impersonation',
    ADMIN_WRITE: 'admin:write',
    ADMIN_READ: 'admin:read',
    BILLING: 'billing:write',
};

export const ORDER_FILTERS = {
    CASH_OUT_ID: -1,
};
export const PAYMENT_STATUS = {
    PAYMENT_INITIALISED: 0,
    CREATOR_FULFILLED: 1,
    PAID_NOT_FULFILLED: 2,
    REFUNDED: 3,
    DISPUTED: 4,
    FAILED: 5,
    PENDING: 6,
};
export const PRICE_OPTION_NAMES = {
    DEFAULT: 'DEFAULT',
    ANNUAL_SUBSCRIPTION: 'ANNUAL_SUBSCRIPTION',
};
export const BOOKING_TYPES = {
    ONE: 'one-on-one',
    GROUP: 'group',
    WEBINAR: 'webinar',
};
export const PAYMENT_TYPES = {
    PAYOUT: 'payout',
    REFERRAL_FEE: 'referral-fee',
    AFFILIATE_FEE: 'affiliate-fee',
    TOPUP: 'topup',
    ACCOUNT_CHARGE: 'account-charge',
    DIGITAL_DOWNLOAD: 'digital-download',
    LEAD_MAGNET: 'lead',
    BOOKING: 'booking',
    FULFILLMENT: 'fulfillment',
    MEMBERSHIP: 'membership',
    COURSE: 'course',
    WEBINAR: 'webinar',
    INVOICE: 'invoice',
    COMMUNITY: 'community',
    RESELL: 'resell',
    AI_AVATAR: 'ai-avatar',
} as const;

export type PaymentProductType = (typeof PAYMENT_TYPES)[keyof typeof PAYMENT_TYPES];

export const STRIPE_DISPUTE_STATUS = {
    NEEDS_RESPONSE: 'needs_response',
    UNDER_REVIEW: 'under_review',
    LOST: 'lost',
    WARNING_NEEDS_RESPONSE: 'warning_needs_response',
    WARNING_UNDER_REVIEW: 'warning_under_review',
    WON: 'won',
} as const;

type EventKeys =
    | 'PAYMENT_EXPECTED_AVAILABLE_TIME'
    | 'PAYMENT_SUCCEEDED'
    | 'PAYOUT_CREATED'
    | 'PAYOUT_UPDATED'
    | 'PAYOUT_PAID'
    | 'PAYOUT_FAILED';

export const STRIPE_EVENT_TYPES: Record<
    EventKeys,
    { STRIPE_CODE: string; ICON_WAITING?: string; ICON_SUCCESS?: string; ICON?: string }
> = {
    PAYMENT_EXPECTED_AVAILABLE_TIME: {
        STRIPE_CODE: 'payment_intent.expected_available_time',
        ICON_WAITING: 'waiting',
        ICON_SUCCESS: 'success',
    },
    PAYMENT_SUCCEEDED: {
        STRIPE_CODE: 'payment_intent.succeeded',
        ICON: 'success',
    },
    PAYOUT_CREATED: {
        STRIPE_CODE: 'payout.created',
        ICON: 'success',
    },
    PAYOUT_UPDATED: {
        STRIPE_CODE: 'payout.updated',
        ICON: 'success',
    },
    PAYOUT_PAID: {
        STRIPE_CODE: 'payout.paid',
        ICON: 'success',
    },
    PAYOUT_FAILED: {
        STRIPE_CODE: 'payout.failed',
        ICON: 'failure',
    },
};
// Custom error messages made to handle these stripe payout errors - https://stripe.com/docs/api/payouts/object#payout_object-failure_code
// Apr 9, 2024: Added more (undocumented) errors based on what we have been getting from Stripe.
export const STRIPE_PAYOUT_ERRORS_AND_RESPONSES_HTML = {
    ACCOUNT_CLOSED:
        'There seems to have been an issue with your bank account. Please try contacting your bank to resolve any issues and try again.',
    ACCOUNT_FROZEN:
        'There seems to have been an issue with your bank account. Please try contacting your bank to resolve any issues and try again.',
    BANK_ACCOUNT_RESTRICTED:
        'There seems to have been an issue with your bank account. Please try contacting your bank to resolve any issues and try again.',
    BANK_OWNERSHIP_CHANGED:
        'There seems to have been an issue with your bank account. Please try contacting your bank to resolve any issues and try again.',
    COULD_NOT_PROCESS:
        'There was an issue verifying your bank details. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    DEBIT_NOT_AUTHORIZED:
        'There was an issue verifying your bank details. Please try contacting your bank to resolve any issues and try again.',
    DECLINED: 'Your bank has declined the payout. Please try contacting your bank to resolve any issues and try again.',
    INSUFFICIENT_FUNDS:
        'Stripe is having trouble processing this payout. Please reach out to <span><a href="mailto:friends@stanwith.me" >friends@stanwith.me</a></span> so we can look into this for you.',
    INVALID_ACCOUNT_NUMBER:
        'There was an issue verifying your bank details. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    INCORRECT_ACCOUNT_HOLDER_DETAILS:
        'There was an issue verifying your bank details. Please try contacting your bank to resolve any issues and try again.',
    INCORRECT_ACCOUNT_HOLDER_NAME:
        'There was an issue verifying your bank details. Please try contacting your bank to resolve any issues and try again.',
    INCORRECT_ACCOUNT_HOLDER_ADDRESS:
        'There was an issue verifying your bank details. Please try contacting your bank to resolve any issues and try again.',
    INCORRECT_ACCOUNT_HOLDER_TAX_ID:
        'There was an issue verifying your bank details. Please try contacting your bank to resolve any issues and try again.',
    INCORRECT_ACCOUNT_TYPE:
        'The account type that we have on file is incorrect. The account type is usually checking or savings. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    INVALID_CURRENCY:
        'There was an issue verifying your bank details. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    NO_ACCOUNT:
        'There was an issue verifying your bank details. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    UNSUPPORTED_CARD:
        'The card on file no longer supports payouts. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    EXPIRED_CARD:
        'There was an issue verifying your card details. Please try updating your bank details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    EXCEEDS_AMOUNT_LIMIT:
        'There was an issue processing this payout. Please reach out to <span><a href="mailto:friends@stanwith.me" >friends@stanwith.me</a></span> so we can look into this for you.',
    LOST_OR_STOLEN_CARD:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    INVALID_CARD:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    REFER_TO_CARD_ISSUER:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    ISSUER_UNAVAILABLE:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    INVALID_ISSUER:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
    INVALID_TRANSACTION:
        'There was an issue verifying your card details. Please try updating your card details <span><a target="_self" href="/account_settings/?tab=store">here</a></span> and try again.',
};
export const PAYMENT_DISCOUNTS = {
    FREE_FOREVER: "Congrats on your free Stan account - it's completely on us!",
};
export const GLOBAL_EVENTS = {
    AUTO_SAVE_RESTORE_MODAL_SHOW: 'auto-save-restore-show-modal',
    AUTO_SAVE_RESTORE: 'auto-save-restore',
    AUTO_SAVE_NO_ACTION: 'auto-save-no-action',
    AUTO_SAVE_START: 'auto-save-start',
};
export const TAX_BEHAVIOUR = {
    INCLUSIVE: 'inclusive',
    EXCLUSIVE: 'exclusive',
};
export const MENUITEM_MORE = {
    id: 'more',
    title: 'More',
    icon: IcPlus,
    route: '/apps',
    scopes: [],
    position: 'top',
    mobileAction: true,
    type: 'menu',
    mobileOrder: 1,
};
export const MENUITEM_IDS = {
    HOME: 'home',
    STORE: 'linksites',
    INCOME: 'income',
    ANALYTICS: 'analytics',
    CUSTOMERS: 'customers',
    COMMUNITY: 'community',
    FUNNELS: 'funnels',
    AFFILIATE: 'affiliate',
    APPOINTMENTS: 'appointments',
    REFERRALS: 'referrals',
    FLOWS: 'email_flow',
    AUTOMATIONS: 'automations',
    CREATOR: 'creator',
    SETTINGS: 'settings',
    ASK_STANLEY: 'ask_stanley',
};
export const MENUITEM_DEFINITIONS: Record<string, MenuItemType> = {
    [MENUITEM_IDS.HOME]: {
        id: MENUITEM_IDS.HOME,
        title: 'Home',
        icon: IcHome,
        route: '/dashboard',
        scopes: [],
        type: 'menu',
        mobileAction: true,
        moduleName: 'general-modules',
        position: 'top',
    },
    [MENUITEM_IDS.STORE]: {
        id: MENUITEM_IDS.STORE,
        title: 'My Store',
        icon: IcStore,
        route: '/stores',
        scopes: [],
        type: 'menu',
        mobileAction: true,
        moduleName: 'general-modules',
        position: 'top',
    },
    [MENUITEM_IDS.INCOME]: {
        id: MENUITEM_IDS.INCOME,
        title: 'Income',
        icon: IcWallet,
        route: '/income',
        scopes: [],
        type: 'menu',
        mobileAction: true,
        moduleName: 'general-modules',
        position: 'top',
    },
    [MENUITEM_IDS.ANALYTICS]: {
        id: MENUITEM_IDS.ANALYTICS,
        title: 'Analytics',
        icon: IcLineChart,
        route: '/analytics',
        scopes: [],
        type: 'menu',
        mobileAction: true,
        moduleName: 'general-modules',
        position: 'top',
    },
    [MENUITEM_IDS.CUSTOMERS]: {
        id: MENUITEM_IDS.CUSTOMERS,
        title: 'Customers',
        icon: IcHeart,
        route: '/audience',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
    },
    [MENUITEM_IDS.COMMUNITY]: {
        id: MENUITEM_IDS.COMMUNITY,
        title: 'Community',
        icon: IcCommunity,
        route: 'external',
        redirect: true,
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'community',
        position: 'top',
    },
    [MENUITEM_IDS.FUNNELS]: {
        id: MENUITEM_IDS.FUNNELS,
        title: 'Funnels',
        subTitle: 'I want to link my pages together!',
        icon: IcFunnel,
        route: '/funnels',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'funnels',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.AFFILIATE]: {
        id: MENUITEM_IDS.AFFILIATE,
        title: 'Affiliate',
        subTitle: 'I want to resell available products!',
        icon: IcLink,
        route: '/affiliate',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'affiliate-tab',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.APPOINTMENTS]: {
        id: MENUITEM_IDS.APPOINTMENTS,
        title: 'Appointments',
        subTitle: 'I want to keep track of my meetings!',
        icon: IcClock,
        route: '/appointments',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.REFERRALS]: {
        id: MENUITEM_IDS.REFERRALS,
        title: 'Referrals',
        subTitle: 'I want to make passive income on Stan!',
        icon: IcSmile,
        route: '/referrals',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.FLOWS]: {
        id: MENUITEM_IDS.FLOWS,
        title: 'Email Flows',
        subTitle: 'I want to send automatic emails!',
        icon: IcMail,
        route: '/flows',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.AUTOMATIONS]: {
        id: MENUITEM_IDS.AUTOMATIONS,
        title: 'AutoDM',
        subTitle: 'I want to send automatic IG replies!',
        icon: IcSend,
        route: '/automations',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.CREATOR]: {
        id: MENUITEM_IDS.CREATOR,
        title: 'Creator',
        subTitle: 'View creator accounts',
        icon: IcPerson,
        route: '/creator',
        scopes: [SCOPES.ADMIN_READ, SCOPES.ADMIN_WRITE, SCOPES.BILLING],
        type: 'admin',
        mobileAction: false,
        moduleName: 'general-modules',
        position: 'top',
        optional: true,
    },
    [MENUITEM_IDS.SETTINGS]: {
        id: MENUITEM_IDS.SETTINGS,
        title: 'Settings',
        icon: IcSettings,
        route: '/account_settings',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        removeFill: true,
        moduleName: 'general-modules',
        position: 'bottom',
    },
    [MENUITEM_IDS.ASK_STANLEY]: {
        id: MENUITEM_IDS.ASK_STANLEY,
        title: 'Ask Stanley',
        icon: IcAskStanley,
        route: '/ask-stanley',
        scopes: [],
        type: 'menu',
        mobileAction: false,
        removeFill: false,
        moduleName: 'general-modules',
        position: 'bottom',
    },
};
export const ALL_MENU_ITEMS: MenuItemType[] = Object.values(MENUITEM_DEFINITIONS);
export const MENUITEMS = {
    top: {
        fixed: buildMenuGroup([
            MENUITEM_IDS.HOME,
            MENUITEM_IDS.STORE,
            MENUITEM_IDS.INCOME,
            MENUITEM_IDS.ANALYTICS,
            MENUITEM_IDS.CUSTOMERS,
            MENUITEM_IDS.COMMUNITY,
        ]),
        optional: buildMenuGroup([
            MENUITEM_IDS.FUNNELS,
            MENUITEM_IDS.AFFILIATE,
            MENUITEM_IDS.APPOINTMENTS,
            MENUITEM_IDS.REFERRALS,
            MENUITEM_IDS.FLOWS,
            MENUITEM_IDS.AUTOMATIONS,
            MENUITEM_IDS.CREATOR,
        ]),
    },
    bottom: {
        fixed: buildMenuGroup([MENUITEM_IDS.SETTINGS]),
        optional: [],
    },
    mobile: {
        primary: buildMenuGroup([MENUITEM_IDS.HOME, MENUITEM_IDS.INCOME, MENUITEM_IDS.STORE, MENUITEM_IDS.ANALYTICS]),
        secondary: buildMenuGroup([
            MENUITEM_IDS.CUSTOMERS,
            MENUITEM_IDS.FUNNELS,
            MENUITEM_IDS.AFFILIATE,
            MENUITEM_IDS.APPOINTMENTS,
            MENUITEM_IDS.REFERRALS,
            MENUITEM_IDS.FLOWS,
            MENUITEM_IDS.AUTOMATIONS,
        ]),
    },
};

export const BOTTOM_MENUITEMS = buildMenuGroup([
    MENUITEM_IDS.HOME,
    MENUITEM_IDS.INCOME,
    MENUITEM_IDS.STORE,
    MENUITEM_IDS.ANALYTICS,
]);

export const AFFILIATE_MENUITEMS = buildMenuGroup(
    [MENUITEM_IDS.AFFILIATE, MENUITEM_IDS.INCOME, MENUITEM_IDS.SETTINGS],
    {
        [MENUITEM_IDS.AFFILIATE]: {
            moduleName: 'affiliate-tab',
        },
        [MENUITEM_IDS.INCOME]: {
            moduleName: 'income-tab',
        },
    }
);
export const INTEGRATION_STATUS = {
    CANCELLED: 0,
    ACTIVE: 1,
    ERRORED: 2,
};
export const INTEGRATION_TYPE = {
    GOOGLE_CALENDAR: 'google-calendar',
    ZOOM: 'zoom',
    STRIPE_CONNECT: 'stripe-connect',
    PAYPAL: 'paypal',
    ZAPIER: 'zapier',
    FACEBOOK: 'facebook',
    INSTAGRAM: 'instagram',
    STAN_PAYMENT: 'stan-payment',
};
export const GENERIC_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    DELETED: 'deleted',
} as const;
export const MINIMUM_ALLOWED_BALANCE = -50; // in dollars
export const PAYMENT_METHOD_TYPES = {
    CARD: 'card',
    KLARNA: 'klarna',
    AFTERPAY: 'afterpay_clearpay',
    IDEAL: 'ideal',
    PAYPAL: 'paypal',
    LINK: 'link',
} as const;
export const DATE_FORMAT = {
    ISO_8601_DATE: 'yyyy-MM-dd',
    USER_DATE: 'MMM d, yyyy',
    CHART_DATE: 'MMM d',
    DATE_TIME_FORMAT_AM_PM: 'yyyy-MM-dd hh:mm aa',
    // Additional formats for filters and UI
    RANGE_SEPARATOR: 'MMM d', // For date range displays like "Jan 15-Jan 20"
    FULL_RANGE: 'MMM d, yyyy', // For full date range displays like "Jan 15, 2024 – Jan 20, 2024"
};
export const STRIPE_ACCOUNT_STATUS = {
    REJECTED: 'rejected',
    RESTRICTED: 'restricted',
    RESTRICTED_SOON: 'restricted-soon',
    INACTIVE: 'inactive',
};
export const LOCAL_STORAGE_KEYS = {
    INTEGRATION_IN_PROGRESS: 'INTEGRATION_IN_PROGRESS',
};
export const PUBLIC_RESOURCES = {
    DEFAULT_REVIEW_IMAGE: 'https://assets.stanwith.me/graphics/icons/new/icon-review-user.jpg',
    REFERRALS_VIDEO_THUMBNAIL: 'https://assets.stanwith.me/live/msc/referrals/THUMB-REFERRAL-2.jpg',
    REFERRALS_VIDEO_URL: 'https://www.youtube.com/embed/cKTxJqpC5BM',
};
export const COURSE_STATUS = {
    PUBLISHED: 1,
    DRAFT: 2,
};
export const COURSE_MODULE_STATUS = {
    PUBLISHED: 1,
    DRAFT: 2,
    DRIP: 3,
};
export const COMMUNITY_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};
export const StoreType = {
    LINKSITE: 'linksite',
    FUNNEL: 'funnel',
} as const;
export const PRODUCT_TYPES = {
    DIGITAL_DOWNLOAD: 'digital-download',
    LEAD_MAGNET: 'lead-magnet',
    BOOKING: 'booking',
    FULFILLMENT: 'fulfillment',
    MEMBERSHIP: 'membership',
    COURSE: 'course',
    WEBINAR: 'webinar',
    COMMUNITY: 'community',
    LINK: 'link',
    STAN_REFERRAL_LINK: 'stan-referral-link',
    AI_AVATAR: 'ai-avatar',
} as const;

export const PRODUCT_TYPE_LABELS = {
    [PRODUCT_TYPES.DIGITAL_DOWNLOAD]: 'Digital Download',
    [PRODUCT_TYPES.LEAD_MAGNET]: 'Lead Magnet',
    [PRODUCT_TYPES.BOOKING]: 'Booking',
    [PRODUCT_TYPES.FULFILLMENT]: 'Fulfillment',
    [PRODUCT_TYPES.MEMBERSHIP]: 'Membership',
    [PRODUCT_TYPES.COURSE]: 'Course',
    [PRODUCT_TYPES.WEBINAR]: 'Webinar',
    [PRODUCT_TYPES.COMMUNITY]: 'Community',
    [PRODUCT_TYPES.LINK]: 'Link',
    [PRODUCT_TYPES.AI_AVATAR]: 'AI Avatar',
} as const;

export const PAGE_DEFAULTS = {
    default: { icon: 'funnel-default', bottom_title: 'My page' },
    'digital-download': { icon: 'funnel-sell-something', bottom_title: 'My sell digital page ' },
    'lead-magnet': { icon: 'funnel-lead-magnet', bottom_title: 'My lead magnet page' },
    meeting: { icon: 'calendar', bottom_title: 'My meeting page' },
    fulfillment: { icon: 'services', bottom_title: 'Sell a Service' },
    membership: { icon: 'membership', bottom_title: 'Recurring Membership' },
    link: { icon: 'link', bottom_title: 'External link / URL' },
    'stan-referral-link': { icon: 'link', bottom_title: 'Stan Affiliate Link' },
    course: { icon: 'course', bottom_title: 'My Course' },
    webinar: { icon: 'webinar', bottom_title: 'My Webinar' },
    community: { icon: 'community', bottom_title: 'My Exclusive Community' },
};

export const THEME_DEFAULTS = {
    DEFAULT_BG: '#f3f6fd',
    DEFAULT_HIGHLIGHT_COLOR: '#5383ff',
    DEFAULT_FONT: 'Grobek',
    DEFAULT_TEXT_COLOR: '#131f60',
};
export const GOOGLE_AD_CONVERSION_ID = 'AW-10854378575';
export const GOOGLE_GA4_MEASUREMENT_ID = 'G-FPXVPVPGVK';
export const GOOGLE_TAG_MANAGER_ID = 'GTM-W9MXN8NB';
export const GOOGLE_AD_CONV_ACTION = {
    STAN: {
        PURCHASE: 'AW-10854378575/R5K_CPads94ZEM_Y4rco',
    },
    STAN_V2: {
        PURCHASE: 'AW-16870002766/CWG9CPK0pZ0aEM6gn-w-',
    },
};

// Ad Tracking Pixel IDs
export const AD_PIXEL_IDS = {
    FACEBOOK: '4296631060464751',
    TIKTOK: 'CVCARNJC77U2KF3E4G70',
    LINKEDIN_PARTNER: '6951194',
};

// LinkedIn Conversion IDs
export const LINKEDIN_CONVERSION_IDS = {
    LOGIN: 20317682,
    ADD_TO_CART: 20317690,
    INITIATE_CHECKOUT: 20317698,
    COMPLETE_REGISTRATION: 20317706,
    START_TRIAL: 20317714,
    ADD_PAYMENT_INFO: 20317770,
    OTHER: 20317722,
} as const;

// Userflow Tokens
export const USERFLOW_TOKENS = {
    PRODUCTION: 'ct_gn77pva5ajcwthimoqmhgcmdee',
    STAGING: 'ct_jvvpyxqbabd6fdu5ikielea7ra',
};

export const ADS_ANALYTIC_EVENTS = {
    LOGIN: 'Login',
    ADD_TO_CART: 'AddToCart',
    INITIATE_CHECKOUT: 'InitiateCheckout',
    COMPLETE_REGISTRATION: 'CompleteRegistration',
    START_TRIAL: 'StartTrial',
    ADD_PAYMENT_INFO: 'AddPaymentInfo',
    OTHER: 'Other',
};
export const MEDIA_CONTENT_MAP = {
    tiktok: { title: 'TikTok', image: '' },
    spotify: { title: 'Spotify', image: '' },
    youtube: { title: 'YouTube', image: '' },
    loom: { title: 'Loom', image: '' },
    wistia: { title: 'Wistia', image: '' },
    'podcast-apple': { title: 'Apple Podcast', image: '' },
    instagram: { title: 'Instagram', image: '' },
    default: { title: 'Media Content', image: '' },
};
export const URL_TO_EMBED_REGEX = {
    youtube:
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|watch)(?:\/|(?:\?v=))|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    loom: /https:\/\/www\.loom\.com\/share\/([a-zA-Z0-9]+)/,
    wistia: /(?:https?:\/\/)?(?:www\.)?(?:wistia\.com\/medias\/|fast\.wistia\.net\/embed\/medias\/)([a-zA-Z0-9]+)(?:[?&].*)?/,
    tiktok: /(?:https?:\/\/)?(?:www\.)?tiktok\.com\//,
    spotify: /(?:https?:\/\/)?(?:open\.)?spotify\.com\//,
    applePodcast: /(?:https?:\/\/)?(?:podcasts\.)?apple\.com\//,
    instagram: /(?:https?:\/\/)?(?:www\.)?instagram\.com\//,
};
export const INVESTIGATION_STATUS = {
    PENDING: 'pending',
    UPHELD: 'upheld',
    RESOLVED: 'resolved',
    CANCELLED: 'cancelled',
    PROCESSED: 'processed',
};
export const DISPUTE_AMOUNT_THRESHOLD = 5000;
export const STANLEY_INTERNAL_PROMPTS = {
    MARKETING_IDEAS: 'marketing_ideas',
    AI_PRODUCT_CREATION: 'aiDigitalDownload',
    GENERATE_IDEAS: 'generate_ideas',
};
export const MFA = {
    DEFAULT_TTL_DAYS: 7,
    FLOW_STAGES: {
        METHOD_CHOICE: 1,
        AUTHENTICATOR_APP: 2,
        OTP_NOTIFICATION: 3,
        VERIFY_CODE: 4,
        SAVE_RECOVERY_CODES: 5,
        COMPLETE_CONFIUGRATION: 6,
    },
    OTP_LENGTH: 6,
};
export const SUBSCRIPTION_PLATFORMS = {
    REVENUE_CAT: 'revenuecat',
    STRIPE: 'stripe',
};
export const EXPERIMENT_GROUP = {
    TREATMENT: 'treatment',
    CONTROL: 'control',
};
export const REFERRAL_DASHBOARD_TEMPLATE_NAMES = {
    STEVEN_BARTLETT_CHALLENGE: 'steven_join_challenge_community',
    GARY_VEE_CHALLENGE: 'gary_vee_challenge',
    AMY_GREGRORY: 'amy_gregrory_store_copy_template',
};
// NOTE: sosb is Stans organic data for Steven Bartlett - username
export const STEVEN_BARTLETT_CHALLENGE_REFERRALS = ['sosb', 'stevenbartlett'];
export const NICHE_LIST = [
    {
        text: '📈 Business',
        name: 'Business Coaching',
        image: '/images/onboarding/jan2023/emoji-chart-increasing.svg',
    },
    {
        text: '💰 Finance',
        name: 'Finance',
        image: '/images/onboarding/jan2023/emoji-moneybag.svg',
    },
    {
        text: '🏠 Real Estate',
        name: 'Real Estate',
        image: '/images/onboarding/jan2023/emoji-house.svg',
    },
    {
        text: '📱 Social Media',
        name: 'Social Media & Marketing',
        image: '/images/onboarding/jan2023/emoji-iphone.svg',
    },
    {
        text: '🏋️‍♂️ Fitness',
        name: 'Fitness',
        image: '/images/onboarding/jan2023/emoji-woman-lifting-weights.svg',
    },
    {
        text: '🧘‍♀️ Wellness',
        name: 'Health & Wellness',
        image: '/images/onboarding/jan2023/emoji-heart.svg',
    },
    {
        text: '🌙 Spirituality',
        name: 'Spirituality & Psychic',
        image: '/images/onboarding/jan2023/emoji-crescent-moon.svg',
    },
    {
        text: '💼 Career',
        name: 'Career & Productivity',
        image: '/images/onboarding/jan2023/emoji-breifcase.svg',
    },
    {
        text: '🧠 Mindset',
        name: 'Mindset & Motivation',
        image: '/images/onboarding/jan2023/emoji-brain.svg',
    },
    {
        text: '🙋 UGC',
        name: 'UGC',
        image: '/images/onboarding/jan2023/emoji-raising-hand.svg',
    },
    {
        text: '📚 Education',
        name: 'Education',
        image: '/images/onboarding/jan2023/emoji-graduation-cap.svg',
    },
    {
        text: '🎶 Arts',
        name: 'Arts',
        image: '/images/onboarding/jan2023/emoji-musical-notes.svg',
    },
    {
        text: '🎉 Events',
        name: 'Events',
        image: '/images/onboarding/jan2023/emoji-party-popper.svg',
    },
    {
        text: '💄 Beauty',
        name: 'Lifestyle, Beauty & Fashion',
        image: '/images/onboarding/jan2023/emoji-lipstick.svg',
    },
    {
        text: '😘 Dating',
        name: 'Relationship & Dating',
        image: '/images/onboarding/jan2023/emoji-kissing-heart.svg',
    },
    {
        text: '🤔 Other',
        name: 'Other',
        image: '/images/onboarding/jan2023/emoji-thinking.svg',
    },
];

export const STAN_TAG_PRESETS: Record<
    string,
    Record<string, { label: string; backgroundColor?: string; textColor?: string }>
> = {
    product: {
        lead: { label: 'Lead', backgroundColor: 'rgba(0, 132, 198, 0.1)', textColor: '#0084C6' },
        booking: { label: 'Meeting', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        'digital-download': {
            label: 'Digital Download',
            backgroundColor: 'rgba(248, 89, 24, 0.1)',
            textColor: 'var(--text-color-notice-fg-notice-bold)',
        },
        membership: { label: 'Membership', backgroundColor: 'rgba(158, 61, 255, 0.1)', textColor: '#9E3DFF' },
        course: { label: 'Course', backgroundColor: 'rgba(101, 116, 255, 0.1)', textColor: '#6574FF' },
        webinar: { label: 'Webinar', backgroundColor: 'rgba(95, 201, 203, 0.1)', textColor: 'rgba(95, 201, 203, 1)' },
        fulfillment: {
            label: 'Fulfillment',
            backgroundColor: 'rgba(95, 201, 203, 0.1)',
            textColor: 'rgba(95, 201, 203, 1)',
        },
        'affiliate-fee': {
            label: 'Affiliate',
        },
        affiliate: {
            label: 'Affiliate',
        },
        invoice: {
            label: 'Invoice',
        },
        community: {
            label: 'Community',
            backgroundColor: 'rgb(231, 242, 254)',
            textColor: 'rgb(87, 131, 255)',
        },
    },
    subscription: {
        totalSubscriptions: { label: 'Subscription', backgroundColor: 'rgba(58, 54, 219, 0.1)', textColor: '#3A36DB' },
    },
    membershipStatus: {
        '0': { label: 'Cancelled', backgroundColor: 'rgba(225, 231, 245, 1)', textColor: '#48638C' },
        '1': { label: 'Active', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        '2': { label: 'Paused', backgroundColor: 'rgba(255, 247, 225, 1)', textColor: '#FFBB00' },
    },
    subscriptionStatus: {
        Active: { label: 'Active', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        Inactive: { label: 'Inactive', backgroundColor: 'rgba(248, 89, 24, 0.1)', textColor: '#F85918' },
    },
    courseProgressStatus: {
        'Not Started': { label: 'Not Started', backgroundColor: '#F3F6FD', textColor: '#8F9CB9' },
        'In Progress': { label: 'In Progress', backgroundColor: '#F3F6FD', textColor: '#6574FF' },
        Completed: { label: 'Completed', backgroundColor: '#DEFFFB', textColor: '#00C6A4' },
        Revoked: { label: 'Revoked', backgroundColor: '#FFDEE2', textColor: '#FF487E' },
    },
    invoiceStatus: {
        paid: { label: 'Paid', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        refunded: { label: 'Refunded', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        draft: { label: 'Draft', backgroundColor: 'rgba(248, 89, 24, 0.1)', textColor: '#F85918' },
        open: { label: 'Open', backgroundColor: 'rgba(248, 89, 24, 0.1)', textColor: '#F85918' },
        void: { label: 'Void', backgroundColor: 'rgba(248, 89, 24, 0.1)', textColor: '#F85918' },
        uncollectible: { label: 'Uncollectible', backgroundColor: 'rgba(248, 89, 24, 0.1)', textColor: '#F85918' },
    },
    emailRecipientStatus: {
        OPEN: { label: 'Opened', backgroundColor: '#EBEBFF', textColor: '#6355FF' },
        CLICK: { label: 'Clicked', backgroundColor: '#E4FFF9', textColor: '#01B88C' },
        SEND: { label: 'Sent', backgroundColor: '#EEF1F5', textColor: '#808EB6' },
        DELIVERY: { label: 'Sent', backgroundColor: '#EEF1F5', textColor: '#808EB6' },
        COMPLAINT: { label: 'Sent', backgroundColor: '#EEF1F5', textColor: '#808EB6' },
        BOUNCE: { label: 'Sent', backgroundColor: '#EEF1F5', textColor: '#808EB6' },
    },
    affiliateSellerStatus: {
        deleted: { label: 'Revoked', backgroundColor: 'rgba(225, 231, 245, 1)', textColor: '#48638C' },
        active: { label: 'Active', backgroundColor: 'rgba(0, 198, 164, 0.1)', textColor: '#00C6A4' },
        not_reseller: { label: 'Not Reseller', backgroundColor: 'rgba(255, 247, 225, 1)', textColor: '#FFBB00' },
    },
    disputeStatus: {
        disputed: {
            label: 'Disputed',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
        under_review: {
            label: 'Disputed',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
        warning_under_review: {
            label: 'Disputed',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
        won: { label: 'Dispute Won', backgroundColor: '#DEFFFB', textColor: '#00C6A4' },
        needs_response: {
            label: 'Needs Response',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
        warning_needs_response: {
            label: 'Needs Response',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
        lost: {
            label: 'Dispute Lost',
            backgroundColor: 'var(--color-negative-dimmed)',
            textColor: 'var(--color-negative-main)',
        },
    },
};

// This will be the list of migrated routes
// REMOVE LOGIN WHEN MOVING FORWARD

export const MIGRATED_ROUTES: string[] = [
    '/account_settings',
    '/analytics',
    '/analytics/affiliate/*',
    '/analytics/course/*',
    '/affiliate/*',
    '/apps/*',
    '/appointments/*',
    '/audience/*',
    '/automations/*',
    '/complete-registration',
    '/dashboard',
    '/flows/*',
    '/forgot_password',
    '/funnels',
    '/funnels/*',
    '/income/*',
    '/login',
    // '/profile',
    '/referrals/*',
    '/register',
    '/reset-password',
    '/subscribe/*',
    '/support/*',
];

// Lowercase list of routes that can be accessed without needing authentication
export const NO_AUTH_ROUTES = [
    '/auth',
    '/login',
    '/register',
    '/forgot_password',
    '/reset-password',
    '/complete-registration',
];

export const QUERY_KEYS = {
    USER: 'user',
    INTEGRATIONS: 'integrations',
    REGISTER: 'register',
    AFFILIATE_USER: 'affiliate-user',
    AFFILIATE_COMPLETE_REGISTRATION: 'affiliate-complete-registration',
    AFFILIATE_TRANSFER_ACCOUNT: 'affiliate-transfer-account',
    SESSION_COUNTRY: 'session-country',
    PASSWORD_LOGIN: 'password-login',
    AUTH_TOKEN_CHECK_UPGRADE: 'auth-token-check-upgrade',
    PASSWORD_RESET: 'password-reset',
    PASSWORD_FORGOT: 'password-forgot',
    COMMUNITIES: 'communities',
    STORE_DATA: 'store-data',
    EMAIL_FLOWS: 'email-flows',
    EMAIL_FLOWS_LIST: 'email-flows-list',
    FLOW_DETAILS: 'flow-details',
    FLOW_ANALYTICS: 'flow-analytics',
    EMAIL_DETAILS: 'email-details',
    EMAIL_TEMPLATE_VARIABLES: 'email-template-variables',
    RECIPIENTS_LIST: 'recipients-list',
    RECIPIENT_COUNT: 'recipient-count',
    FAN_TAGS: 'fan-tags',
    FUNNELS: 'funnels',
    HAS_DISPUTE: 'has-dispute',
    EXPERIMENTS: 'experiments',
    BROADCASTS: 'broadcasts',
    BALANCE: 'balance',
    ORDERS: 'orders',
    PAYMENT_DETAILS: 'payment-details',
    COURSE_LINK: 'course-link',
    COURSE_LINK_PAYMENT: 'course-link-payment',
    DIGITAL_DOWNLOAD: 'digital-download',
    BOOKING: 'booking',
    CURRENCIES: 'currencies',
    LOGIN_SESSIONS: 'login-sessions',
    SEND_RECEIPT: 'send-receipt',
    DOWNLOAD_RECEIPT: 'download-receipt',
    BILLING_LINK: 'billing-link',
    PAYMENT_PLAN: 'payment-plan',
    REFUND_ORDER: 'refund-order',
    FULFILL_ORDER: 'fulfill-order',
    UNFULFILL_ORDER: 'unfulfill-order',
    RESEND_ORDER: 'resend-order',
    DISPUTE_EVIDENCE: 'dispute-evidence',
    SUBMIT_DISPUTE: 'submit-dispute',
    REVENUE_CHART: 'revenue-chart',
    ANALYTICS_METRICS: 'analytics-metrics',
    ANALYTICS_CHART: 'analytics-chart',
    ANALYTICS_MULTIPLE_CHARTS: 'analytics-multiple-charts',
    ANALYTICS_PRODUCT_ANALYTICS: 'analytics-product-analytics',
    AUDIENCE: 'audience',
    CUSTOMER_DETAILS: 'customer-details',
    USER_INVOICES: 'user-invoices',
    SUBSCRIPTION_DETAILS: 'subscription-details',
    STRIPE_CUSTOMER_ID: 'stripe-customer-id',
    STRIPE_CONNECT_STATUS: 'stripe-connect-status',
    REFERRALS: 'referrals',
    REFERRALS_ANALYTICS: 'referrals-analytics',
    // Analytics/Course related
    COURSE_ANALYTICS: 'course-analytics',
    COURSE_FANS: 'course-fans',
    COURSE_EMAIL_TEMPLATE: 'course-email-template',
    AFFILIATE_EMAIL_TEMPLATE: 'affiliate-email-template',
    MY_AFFILIATE_LINKS: 'my-affiliate-links',
    STORES: 'stores',
    UNSPLASH_IMAGES: 'unsplash-images',
    UNSPLASH_IMAGE_API_COMPLIANCE: 'unsplash-image-api-compliance',
    BOOKINGS: 'bookings',
    BLOCKED_TIMES: 'blocked-times',
    AVAILABILITY: 'availability',
    AUTOMATIONS: 'automations',
    AUTOMATION_DETAILS: 'automation-details',
    AUTOMATION_ANALYTICS: 'automation-analytics',
    INSTAGRAM_POSTS: 'instagram-posts',
    INSTAGRAM_BUSINESS_ACCOUNTS: 'instagram-business-accounts',
    WEEKLY_INSTAGRAM_POSTS: 'weekly-instagram-posts',
    PROMO_CODE_DETAILS: 'promo-code-details',
    TAX_DOCUMENTS: 'tax-documents',
    TAX_SETTINGS: 'tax-settings',
    MFA_LIST: 'mfa-list',
    MFA_RECOVERY: 'mfa-recovery',
    MFA_VALIDATE_OTP: 'mfa-validate-otp',
};

export const SENTRY_FEATURES = {
    ANALYTICS: 'analytics',
    INCOME: 'income',
    DISPUTE: 'dispute',
    FILE_UPLOAD: 'file-upload',
    STORE_DATA: 'store-data',
    FUNNELS: 'funnels',
    EMAIL_FLOWS: 'email-flows',
    AUDIENCE: 'audience',
    REFERRALS: 'referrals',
    MY_STORE: 'my-store',
    COURSE_ANALYTICS: 'course-analytics',
    AFFILIATE_LINKS: 'affiliate-links',
    COMMUNITIES: 'communities',
    AUTOMATIONS: 'automations',
    AUTHENTICATION: 'authentication',
    SETTINGS: 'settings',
};

export const CUSTOMER_SUBSCRIPTION_STATUS = {
    CANCELLED: 0,
    ACTIVE: 1,
    PAUSED: 2,
};

export const CUSTOMER_SUBSCRIPTION_INTERVALS = {
    DAY: 'day',
    WEEK: 'week',
    BIWEEK: 'bi-week',
    MONTH: 'month',
    YEAR: 'year',
};

export const CUSTOMER_SUBSCRIPTION_INTERVAL_LABELS: Record<string, string> = {
    [CUSTOMER_SUBSCRIPTION_INTERVALS.DAY]: 'Daily',
    [CUSTOMER_SUBSCRIPTION_INTERVALS.WEEK]: 'Weekly',
    [CUSTOMER_SUBSCRIPTION_INTERVALS.BIWEEK]: 'Bi-weekly',
    [CUSTOMER_SUBSCRIPTION_INTERVALS.MONTH]: 'Monthly',
    [CUSTOMER_SUBSCRIPTION_INTERVALS.YEAR]: 'Annually',
};

export const CUSTOMER_SUBSCRIPTION_STATUS_LABELS: Record<number, string> = {
    [CUSTOMER_SUBSCRIPTION_STATUS.CANCELLED]: 'Cancelled',
    [CUSTOMER_SUBSCRIPTION_STATUS.ACTIVE]: 'Active',
    [CUSTOMER_SUBSCRIPTION_STATUS.PAUSED]: 'Paused',
};

export const CUSTOMER_SUBSCRIPTION_STATUS_TAG_CLASS: Record<number, string> = {
    [CUSTOMER_SUBSCRIPTION_STATUS.CANCELLED]: 'bg-neutral-subtle text-dark',
    [CUSTOMER_SUBSCRIPTION_STATUS.ACTIVE]: 'bg-seafoam-subtle text-success-main',
    [CUSTOMER_SUBSCRIPTION_STATUS.PAUSED]: 'bg-warning-subtle text-warning-dark',
};

export const REGEX = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// Legal document URLs
export const LEGAL_URLS = {
    TERMS_OF_SERVICE: 'https://assets.stanwith.me/legal/terms-of-service.pdf',
    PRIVACY_POLICY: 'https://assets.stanwith.me/legal/privacy-policy.pdf',
};

// Export date format constants
export { DATE_FORMATS } from './dateFormats';
